import React, { useState, useEffect } from "react";
import { ErrorResponse } from "../../../Models/Error.js";
import { Shipment } from "../../../Models/Shipment.js";
import { useAppContext } from "../../../AppProvider.jsx";
import {
  callSuccessToast,
  callErrorToast,
} from "../../../utilities/utilities.ts";
import { ShipmentsService } from "../../../services/shipment/shipments.ts";
import { useNavigate } from "react-router-dom";
import {
  ShipmentConfirmContextValue,
  ShipmentsConfirmProviderProps,
} from "./types.ts";
import { ShipmentConfirmContext } from "./contexts.ts";

const shipmentsService = new ShipmentsService();

export const ShipmentConfirmProvider: React.FC<
  ShipmentsConfirmProviderProps
> = ({ children, data = [], callback }) => {
  const navigate = useNavigate();
  const { dictionary } = useAppContext();
  const [error, setError] = useState<ErrorResponse<Shipment> | null>(null);
  const [loader, setLoader] = useState<boolean>(false);

  const confirmShipments = async (): Promise<void> => {
    setLoader(true);

    try {
      // Execute all confirm requests concurrently
      const results = await Promise.allSettled(
        data.map((id) => shipmentsService.confirm(id)),
      );

      // Separate results into successful and rejected categories
      const failed = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const success = results.filter(
        (r): r is PromiseFulfilledResult<void> => r.status === "fulfilled",
      );

      // If at least one confirmation failed
      if (failed.length > 0) {
        const firstError = failed[0].reason as ErrorResponse<Shipment>;
        setError(firstError);

        // If some confirmations succeeded, refresh UI/state
        if (success.length > 0 && callback) {
          callback();
        }

        return;
      }

      // If all confirmations were successful
      if (callback) callback();
      callSuccessToast(dictionary.messages.save_successful);
      navigate("/shipments");
    } catch (unexpected) {
      // Generic error fallback for unexpected exceptions
      const err: ErrorResponse<Shipment> = {
        response: {
          data: {},
        },
      };

      setError(err);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  const contextValue: ShipmentConfirmContextValue = {
    data,
    loader,
    confirmShipments,
    callback,
  };

  return (
    <ShipmentConfirmContext.Provider value={contextValue}>
      {children}
    </ShipmentConfirmContext.Provider>
  );
};
